import MentorshipService, {
    MENTOR_POOL_REMINDER_NOT_CONFIGURED,
    MentorshipMentorPoolMember,
} from './MentorshipService';

interface MentorshipQuickMentorProfileProps {
    open: boolean;
    member: MentorshipMentorPoolMember | null;
    onClose: () => void;
    onEditTracks: () => void;
    onEditStatus: () => void;
    onRemove: () => void;
    onSendMessage: () => void;
    onSendInvite: () => void;
    inviting?: boolean;
}

const Field = ({ label, value }: { label: string; value: string }) => (
    <div className="mb-3">
        <div className="small text-muted">{label}</div>
        <div>{value}</div>
    </div>
);

const MentorshipQuickMentorProfile = ({
    open,
    member,
    onClose,
    onEditTracks,
    onEditStatus,
    onRemove,
    onSendMessage,
    onSendInvite,
    inviting,
}: MentorshipQuickMentorProfileProps) => {
    const alumni = member?.alumni;
    const photo = MentorshipService.profilePictureSrc(alumni?.profilePicture);

    return (
        <>
            <div
                className={`iar-backdrop${open ? ' show' : ''}`}
                style={{ display: open ? 'block' : 'none' }}
                onClick={onClose}
            />
            <div
                className={`offcanvas offcanvas-end bg-white${open ? ' show' : ''}`}
                tabIndex={-1}
                style={{ width: 480, maxWidth: '95vw', visibility: open ? 'visible' : 'hidden' }}>
                <div className="offcanvas-header border-bottom">
                    <h5 className="offcanvas-title font-16 font-600 mb-0">Quick Mentor Profile</h5>
                    <button type="button" className="btn-close" aria-label="Close" onClick={onClose} />
                </div>
                <div className="offcanvas-body">
                    {!member ? (
                        <div className="text-muted">Select a mentor to view profile details.</div>
                    ) : (
                        <>
                            <div className="d-flex align-items-center gap-3 mb-4">
                                {photo ? (
                                    <img
                                        src={photo}
                                        alt=""
                                        width={64}
                                        height={64}
                                        className="rounded-circle"
                                        style={{ objectFit: 'cover' }}
                                    />
                                ) : (
                                    <div
                                        className="rounded-circle bg-light d-flex align-items-center justify-content-center"
                                        style={{ width: 64, height: 64 }}>
                                        {(alumni?.name || '?').slice(0, 1).toUpperCase()}
                                    </div>
                                )}
                                <div>
                                    <div className="font-18 font-600">{alumni?.name || '—'}</div>
                                    <div className="small text-muted">{alumni?.uuid || '—'}</div>
                                </div>
                            </div>

                            <Field label="Program" value={alumni?.programName || '—'} />
                            <Field label="Batch" value={alumni?.batchYear == null ? '—' : String(alumni.batchYear)} />
                            <Field
                                label="Current Role"
                                value={MentorshipService.displayCurrentRole(alumni?.company, alumni?.designation)}
                            />
                            <Field
                                label="Experience"
                                value={alumni?.totalWorkex == null ? '—' : `${alumni.totalWorkex}`}
                            />
                            <Field
                                label="Status"
                                value={MentorshipService.displayConfirmationStatus(member.confirmationStatus)}
                            />
                            <Field label="AP" value={alumni?.ap == null ? '—' : String(alumni.ap)} />
                            <Field label="MQ" value={alumni?.mq == null ? '—' : String(alumni.mq)} />
                            <Field label="VL" value={alumni?.vl == null ? '—' : String(alumni.vl)} />
                            <Field label="Source" value={member.source || '—'} />
                            <Field
                                label="Mentees Assigned"
                                value={MentorshipService.displayAssignedMentees(member.assignedMentees)}
                            />
                            <Field
                                label="Capacity"
                                value={MentorshipService.displayCapacity(member.maxMenteesPerMentor)}
                            />

                            <div className="mb-3">
                                <div className="d-flex justify-content-between align-items-center">
                                    <div className="small text-muted">Career Tracks</div>
                                    <button type="button" className="btn btn-link btn-sm p-0" onClick={onEditTracks}>
                                        Edit
                                    </button>
                                </div>
                                <div>
                                    {member.tracks.length
                                        ? member.tracks.map((track) => track.name).filter(Boolean).join(', ')
                                        : '—'}
                                </div>
                            </div>

                            <div className="d-flex flex-wrap gap-2 mt-4">
                                <button type="button" className="btn btn-outline-secondary btn-sm" onClick={onSendMessage}>
                                    Send Message
                                </button>
                                <button type="button" className="btn btn-outline-secondary btn-sm" onClick={onEditStatus}>
                                    Update Status
                                </button>
                                <button
                                    type="button"
                                    className="btn btn-outline-secondary btn-sm"
                                    disabled={Boolean(inviting)}
                                    onClick={onSendInvite}>
                                    {inviting ? 'Inviting…' : 'Send Invite'}
                                </button>
                                <button
                                    type="button"
                                    className="btn btn-outline-secondary btn-sm"
                                    disabled
                                    title={MENTOR_POOL_REMINDER_NOT_CONFIGURED}>
                                    Send Reminder
                                </button>
                                <button type="button" className="btn btn-outline-danger btn-sm" onClick={onRemove}>
                                    Remove from Pool
                                </button>
                            </div>
                        </>
                    )}
                </div>
            </div>
        </>
    );
};

export default MentorshipQuickMentorProfile;
